/*
 * NewContact.tsx
 * 
 * Features:
 * - Modal form for creating new contacts
 * - Account search and selection functionality with keyboard navigation
 * - Dynamic form generation based on team schemas
 * - Integration with initial email and name from external sources
 * 
 * Logic:
 * - Loads accounts on modal open and filters by search terms
 * - Requires account selection before showing contact form
 * - Uses team-specific schemas for dynamic form generation
 * - Supports keyboard navigation (arrows, enter, escape) in account dropdown
 * - Automatically resets all state when modal closes to ensure clean start
 * 
 * Changes:
 * - Added useEffect to reset selectedAccount, accountSearch, and highlightedIndex when modal closes
 * - This ensures that when reopening the modal, no previous selections persist
 * - Fixed Cancel button functionality to fully reset modal state
 * - Added account-scoped mode via `initialAccount` + `lockInitialAccount`
 * - Added optional `onCreated` callback for post-create refresh without full page reload
 * - Matches the compact direct-form presentation used by the New Account dialog
 * - Preserves the title's top inset while removing its extra visible bottom space
 * - Displays the selected account name and parenthesized address on one line
 * - Reuses the global account Query and refreshes contact lists after creation without reloading
 * - Reuses the shared 65,536-character Prospect Note schema limit
 */

import { FC, useState, useEffect, useMemo, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { THEME_CLASSES } from '../../constants/themeConstants';
import SchemaBasedDetails from '../common/SchemaBasedDetails';
import { useTeams } from '../../context/TeamContext';
import { useSchemas } from '../../context/SchemaContext';
import { Account } from '../../models/Account';
import { renderTypeChip } from '../../utils/typeChipUtils';
import { Button, DialogShell, Input } from '../primitives';
import { useGlobalAccountsQuery } from '../../query/accountQueries';
import { useCreateContactMutation } from '../../query/contactQueries';
import { applyProspectContactNoteLimit } from '../../utils/schemaFieldLimits';

interface NewContactProps {
  isOpen: boolean;
  onClose: () => void;
  initialEmail?: string;
  initialName?: string;
  initialAccount?: {
    id: number;
    name: string;
    teamId?: number | null;
    city?: string | null;
    province?: string | null;
    rlpCompanyId?: number | null;
  };
  lockInitialAccount?: boolean;
  onCreated?: () => void | Promise<void>;
}

type AccountOption = {
  id: number;
  name: string;
  teamId?: number | null;
  city?: string | null;
  province?: string | null;
  rlpCompanyId?: number | null;
};

const NewContact: FC<NewContactProps> = ({
  isOpen,
  onClose,
  initialEmail,
  initialName,
  initialAccount,
  lockInitialAccount = false,
  onCreated
}) => {
  const { currentUserId } = useTeams();
  const accountsQuery = useGlobalAccountsQuery(isOpen && !initialAccount);
  const createContact = useCreateContactMutation();
  const accounts: Account[] | null = accountsQuery.data ?? null;
  const [accountSearch, setAccountSearch] = useState('');
  const [selectedAccount, setSelectedAccount] = useState<AccountOption | null>(null);
  const { schemas, isLoading: isLoadingSchemas } = useSchemas(1);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const selectedAccountLocation = selectedAccount
    ? [selectedAccount.city, selectedAccount.province].filter(Boolean).join(', ')
    : '';

  // Reset state when modal closes and preselect account when provided.
  useEffect(() => {
    if (!isOpen) {
      setSelectedAccount(null);
      setAccountSearch('');
      setHighlightedIndex(-1);
      return;
    }

    if (initialAccount) {
      setSelectedAccount(initialAccount);
      setAccountSearch('');
      setHighlightedIndex(-1);
    }
  }, [isOpen, initialAccount]);

  // Filter accounts based on search
  const filteredAccounts = useMemo(() => {
    if (!accounts || !accountSearch) return [];
    const searchLower = accountSearch.toLowerCase();
    return accounts.filter(account =>
      !account.rlpCompanyId && (
        account.name?.toLowerCase().includes(searchLower) ||
        account.city?.toLowerCase().includes(searchLower) ||
        account.province?.toLowerCase().includes(searchLower))
    ).slice(0, 5); // Limit to 5 results
  }, [accounts, accountSearch]);

  // Update highlighted index when filtered accounts change
  useEffect(() => {
    setHighlightedIndex(filteredAccounts.length > 0 ? 0 : -1);
  }, [filteredAccounts]);

  const formSchemas = useMemo(() => {
    if (!selectedAccount) return [];

    // Get team schema for the account's team
    const teamSchema = schemas.find(s =>
      s.entityType === 1 && s.teamId === selectedAccount.teamId
    );
    return teamSchema ? [applyProspectContactNoteLimit(teamSchema)] : [];
  }, [selectedAccount, schemas]);

  if (!isOpen) return null;

  const handleSubmit = async (formData: Record<string, any>) => {
    if (!currentUserId || !selectedAccount) return;

    try {
      const contactData = {
        ...formData,
        teamId: selectedAccount.teamId
      };

      await createContact.mutateAsync({
        name: formData.name || formData.displayName || '',
        data: JSON.stringify(contactData),
        accountId: selectedAccount.id
      });

      if (onCreated) {
        await onCreated();
      }

      onClose();
    } catch (error) {
      console.error('Error creating contact:', error);
      throw error;
    }
  };

  const selectAccount = (account: AccountOption) => {
    setSelectedAccount(account);
    setAccountSearch('');
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (filteredAccounts.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredAccounts.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : filteredAccounts.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredAccounts.length) {
          selectAccount(filteredAccounts[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setAccountSearch('');
        break;
    }
  };

  return (
    <DialogShell
      open={isOpen}
      title="New Contact"
      onClose={onClose}
      panelClassName="max-w-[800px] flex flex-col"
      headerClassName="!px-2 !pt-2 !pb-0"
      titleClassName="text-xl"
      closeButtonClassName="!h-7 !w-7"
      bodyClassName="!pt-0 min-h-0 flex-1"
    >

      <div className="mb-1">
        {!selectedAccount ? (
          <div className="flex items-center gap-3">
            <label className={`text-sm my-auto font-medium whitespace-nowrap ${THEME_CLASSES.text.secondary}`}>
              Search Account:
            </label>
            <div className="relative flex-1">
              <Input
                type="text"
                value={accountSearch}
                onChange={(e) => setAccountSearch(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search by account name, city, or province..."
              />
              {filteredAccounts.length > 0 && (
                <div className={`absolute z-10 w-full mt-1 rounded-lg shadow-lg border ${THEME_CLASSES.dropdown.background} ${THEME_CLASSES.card.border}`}>
                  {filteredAccounts.map((account, index) => (
                    <button
                      key={account.id}
                      onClick={() => selectAccount(account)}
                      className={`w-full text-left px-4 py-2 ${THEME_CLASSES.dropdown.item.base} ${index === highlightedIndex
                        ? THEME_CLASSES.dropdown.item.hover.split('hover:').join('')
                        : THEME_CLASSES.dropdown.item.hover
                        }`}
                    >
                      <div className="flex items-center gap-2">
                        {renderTypeChip({
                          rlpCompanyId: account.rlpCompanyId,
                          teamId: account.teamId
                        })}
                        <span className="text-sm font-medium">{account.name}</span>
                      </div>
                      <div className={`text-xs ${THEME_CLASSES.text.secondary}`}>
                        {account.city}, {account.province}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className={`mt-2 flex justify-between items-center p-2 rounded-lg ${THEME_CLASSES.note.card.background.gray}`}>
            <div className="flex min-w-0 items-center gap-2 whitespace-nowrap">
              {renderTypeChip({
                rlpCompanyId: selectedAccount.rlpCompanyId,
                teamId: selectedAccount.teamId
              })}
              <span className={`font-medium ${THEME_CLASSES.text.primary}`}>{selectedAccount.name}</span>
              {selectedAccountLocation && (
                <span className={`text-sm ${THEME_CLASSES.text.secondary}`}>
                  ({selectedAccountLocation})
                </span>
              )}
            </div>
            {!lockInitialAccount && (
              <Button
                onClick={() => setSelectedAccount(null)}
                variant="icon"
                aria-label="Clear selected account"
              >
                ×
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="min-h-[300px]">
        {selectedAccount && formSchemas.length > 0 ? (
          <SchemaBasedDetails
            schemas={formSchemas}
            data={{
              email: initialEmail,
              displayName: initialName
            }}
            onSubmit={handleSubmit}
            onCancel={onClose}
            isDeleteEnabled={false}
            isDisabled={() => false}
            showSectionHeaders={false}
            showCardSurface={false}
            constrainHeight={false}
            constrainFieldGrid={false}
            fieldTextSize="sm"
            compactRowSpacing
          />
        ) : (
          <div className={`flex items-center justify-center h-full ${THEME_CLASSES.text.secondary}`}>
            {isLoadingSchemas ? 'Loading schema...' : 'Please select an account first'}
          </div>
        )}
      </div>
    </DialogShell>
  );
};

export default NewContact;
